/* eslint-disable react-doctor/no-inline-exhaustive-style, react-doctor/no-tiny-text */
"use client";

import { useState, useEffect, useCallback, useRef, startTransition, type CSSProperties } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { LazyMotion, domAnimation, m, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Download,
  Share2,
  Flag,
  Heart,
} from "lucide-react";
import { Chrome } from "@/components/ui/tls/chrome";
import { Mono } from "@/components/ui/tls/mono";
import { TPlay } from "@/components/ui/tls/t-play";
import { Breadcrumb } from "@/components/plate-and-hairline/page-top";
import { PlateHeading } from "@/components/plate-and-hairline/plate-heading";
// The Archivo loader cannot be imported here: next/font must be called from a
// Server Component. app/layout.tsx applies the font variable on <html>, and
// this file only consumes var(--ls-font).
import { fmtFileSize } from "@/lib/tls-format";
import { visibleDownloadCount } from "@/lib/format-count";
import { previewFailureMessage } from "@/lib/state-copy";
import { getDurationHint, getInstallHint, trimDownloadSpan } from "@/lib/sound-utils";
import { Spinner } from "@/components/ui/spinner";
import { EmailSoundForm } from "@/components/ui/email-sound-form";
import { InstallCheckin } from "@/components/ui/install-checkin";
import { buildSurfaceShareUrl } from "@/lib/social-share-copy";

const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL?.replace(/\/$/, "") || "https://teslalocksound.com";
import { cn } from "@/lib/utils";
import { useAudioPlayerContext } from "@/contexts/audio-player-context";
import { announce } from "@/lib/announce";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { useAudioUrl } from "@/hooks/useAudioUrl";
import { useRealtimeDownloads } from "@/hooks/useRealtimeDownloads";
import { useSoundDownload } from "@/hooks/sound-player/useSoundDownload";
import { type Sound } from "@/lib/supabase";
import { categoryBrowseHref } from "@/lib/category-data";
import { getSoundCrossLink } from "@/lib/sound-cross-links";
import { TESLA_REQUIREMENTS } from "@/lib/audio-utils";
import InstallWarnings from "@/components/sound-player/install-warnings";
import { PlayerSeek, PlayerTime } from "@/components/ui/audiocn/player";
import { useFavoriteSounds } from "@/hooks/useFavoriteSounds";
import {
  trackSavedSoundSaveClick,
  trackSavedSoundUnsaveClick,
  trackSoundPageViewed,
  trackPreviewPlay,
  trackReferralLinkGenerated,
  trackReferralLinkCopied,
} from "@/lib/tracking";
import { MOTION_DURATION, MOTION_EASE, MOTION_STAGGER } from "@/lib/motion-theme";
import { OpenInChimeStudio } from "@/components/create/open-in-chime-studio";
import { RelatedSoundsRail } from "@/components/sound-player/related-sounds-rail";
import { itemVariants, iconButtonClass } from "@/components/sound-player/shared-motion";
import { SpecSheetPanel } from "@/components/sound-player/spec-sheet-panel";
import { StickyInstallCta } from "@/components/sound-player/sticky-install-cta";
import { PostDownloadReceipt } from "@/components/ui/post-download-receipt";
import { play } from "cuelume";
import { isMatureSound, MATURE_LABEL_DETAIL } from "@/lib/mature-content";

interface SoundPlayerClientProps {
  sound: Sound;
  relatedSounds?: Sound[];
  downloadOnly?: boolean;
  /**
   * Audio URL pre-resolved server-side (TES-831). Seeds useAudioUrl state
   * immediately — no client-side async waterfall on first load.
   */
  initialAudioUrl?: string | null;
  /** Pre-resolved related-rail heading (TES-810b waterfall). */
  relatedRailTitle?: string;
}

const EMPTY_RELATED_SOUNDS: Sound[] = [];

// TES-831: these are never part of first paint — modals open on interaction,
// the trimmer only renders for >5s sounds, the wizard sits in a below-fold
// accordion. Code-splitting them out shrinks the initial JS bundle that
// blocks hydration/TBT on mobile (461KB unused JS in the Lighthouse trace).
const AudioTrimmer = dynamic(
  () => import("@/components/ui/audio-trimmer").then((m) => m.AudioTrimmer),
  { loading: () => null }
);
const ReportModal = dynamic(() => import("@/components/ui/report-modal"), {
  loading: () => null,
});
const ShareSetupPanel = dynamic(
  () => import("@/components/ui/share-setup-panel").then((m) => m.ShareSetupPanel),
  { loading: () => null }
);

// Hoisted to module scope so variant objects are stable across renders —
// prevents Framer Motion from re-evaluating stagger on every state change.
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: MOTION_STAGGER.relaxed },
  },
};

/**
 * A failure note lands under the button that was pressed, which on a phone can
 * be below the fold. Bring it into view only if it is out of view; the
 * scroll-padding on <html> keeps it clear of the nav and the docked bars.
 */
const revealNote = (el: HTMLElement | null) => {
  el?.scrollIntoView?.({ block: "nearest" });
};

export default function SoundPlayerClient({
  sound,
  relatedSounds = EMPTY_RELATED_SOUNDS,
  downloadOnly = false,
  initialAudioUrl,
  relatedRailTitle,
}: SoundPlayerClientProps) {
  // Global audio player context
  const globalPlayer = useAudioPlayerContext();
  const prefersReducedMotion = useReducedMotion();

  // Check if THIS sound is currently active in the global player
  const isThisSoundPlaying = globalPlayer.currentSound?.id === sound?.id && globalPlayer.isPlaying;
  const isThisSoundLoading = globalPlayer.currentSound?.id === sound?.id && globalPlayer.isLoading;
  const isThisSoundActive = globalPlayer.currentSound?.id === sound?.id;

  // TES-2556: cross-link to a near-duplicate sound that splits the same query.
  const crossLink = getSoundCrossLink(sound.slug);

  // Trim state for sounds longer than 5 seconds
  const needsTrimming = (sound.duration || 0) > TESLA_REQUIREMENTS.MAX_DURATION;
  const [trimStart, setTrimStart] = useState(0);
  const defaultTrimEnd = Math.min(
    TESLA_REQUIREMENTS.MAX_DURATION,
    sound.duration || TESLA_REQUIREMENTS.MAX_DURATION
  );
  const [trimEnd, setTrimEnd] = useState(defaultTrimEnd);

  // Trim preview mode state
  const [trimModeActive, setTrimModeActive] = useState(false);
  const [loopEnabled, setLoopEnabled] = useState(true);

  // Breadcrumb middle segment: sound-card.tsx tags detail links with
  // ?from=saved when the card was rendered on /sounds/saved (TES noted P3 —
  // "dead ?from=saved param"). Read client-side (this component is already
  // "use client") rather than useSearchParams, so no Suspense boundary is
  // required and the initial render stays SSR-safe (defaults to /sounds).
  const [breadcrumbSource, setBreadcrumbSource] = useState<{ label: string; href: string }>({
    label: "Sounds",
    href: "/sounds",
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("from") === "saved") {
      // Post-hydration URL read; transition avoids a synchronous cascade.
      startTransition(() => {
        setBreadcrumbSource({ label: "Saved", href: "/sounds/saved" });
      });
    }
  }, []);

  // The receipt's "You're set." heading. The receipt focuses it on mount
  // (§05.3); the sticky mobile CTA points back at it once the download is
  // done, so the strip never re-offers a file the visitor already has.
  const receiptHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const showInstallSteps = useCallback(() => {
    const el = receiptHeadingRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "start", behavior: prefersReducedMotion ? "auto" : "smooth" });
    el.focus({ preventScroll: true });
  }, [prefersReducedMotion]);

  const [phoneEmailOpen, setPhoneEmailOpen] = useState(false);
  const trimmerRef = useRef<HTMLDivElement | null>(null);

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);

  // Share setup modal state
  const [showShareSetup, setShowShareSetup] = useState(false);

  const { isFavorite, toggleFavorite, hasHydrated } = useFavoriteSounds();
  const isSaved = hasHydrated && isFavorite(sound.id);
  const [interactionNotice, setInteractionNotice] = useState<string | null>(null);

  // Get audio URL for local trim player. initialAudioUrl (resolved server-side)
  // seeds state immediately — no async waterfall on first load (TES-831).
  const { audioUrl } = useAudioUrl(sound.file_url, initialAudioUrl);

  // Real-time download count with optimistic updates.
  // Only open a Supabase Realtime channel for sounds with meaningful download
  // velocity (>50). The long tail rarely gets a live download during a session,
  // so a persistent socket per visit is wasted connection overhead — the SSR'd
  // count still renders and optimistic local updates still fire on download.
  const { downloads: liveDownloads, triggerLocalDownload } = useRealtimeDownloads(
    sound.id,
    sound.downloads || 0,
    { enabled: (sound.downloads || 0) > 50 }
  );

  // §5.10 — post-download follow-on line; omitted when no related data exists.
  const followOnSound = relatedSounds[0];
  // One file-size format on the whole template (F-4): "342 KB" / "1.2 MB".
  const downloadSizeLabel = fmtFileSize(sound.size);

  // The inline receipt is the sole completion surface. It keeps the install
  // path in context instead of a detached confirmation.
  const { isDownloading, downloadError, downloadCompleted, deliveredBytes, handleDownload } =
    useSoundDownload({
      sound,
      needsTrimming,
      trimStart,
      trimEnd,
      triggerLocalDownload,
    });

  // What the CTA promises after the file name. Over the 5s cap the hook
  // trims on download, so the trailing figure is the length that will be
  // delivered rather than the size of a file the visitor will not get;
  // otherwise it is the catalog size.
  const trimSpan = trimDownloadSpan(trimStart, trimEnd);
  // The editor carries its own Download only once the visitor has moved the
  // window or opened the preview; at rest the page has one Download.
  const trimEdited =
    trimModeActive ||
    trimStart !== 0 ||
    trimEnd !== defaultTrimEnd;
  // What the Download hands over. A long sound is cut at download, so the
  // catalog file's size and length describe a file the visitor never gets.
  const deliveredSeconds = needsTrimming ? Math.max(0, trimEnd - trimStart) : sound.duration || 0;
  const deliveredSizeLabel = needsTrimming ? "" : downloadSizeLabel;
  const ctaTrailing = needsTrimming ? trimSpan : downloadSizeLabel;
  // The receipt's serial line prints the size of the file that was actually
  // handed over.
  const receiptSound = deliveredBytes != null ? { ...sound, size: fmtFileSize(deliveredBytes) } : sound;

  // Local audio player for trim preview mode with boundaries
  const trimPlayer = useAudioPlayer({
    src: trimModeActive ? audioUrl || undefined : undefined,
    soundId: sound.id,
    boundaries: trimModeActive
      ? {
          start: trimStart,
          end: trimEnd,
          loop: loopEnabled,
        }
      : undefined,
    onBoundaryReached: () => {
      // Callback when boundary is reached (for analytics or UI feedback)
    },
  });

  // Track the previous trim bounds to detect significant changes
  const prevTrimBoundsRef = useRef({ start: trimStart, end: trimEnd });

  // Handle trim changes from AudioTrimmer
  const handleTrimChange = useCallback(
    async (start: number, end: number) => {
      const prevBounds = prevTrimBoundsRef.current;
      const boundsChangedSignificantly =
        Math.abs(start - prevBounds.start) > 0.1 || Math.abs(end - prevBounds.end) > 0.1;

      setTrimStart(start);
      setTrimEnd(end);
      prevTrimBoundsRef.current = { start, end };

      // If in trim mode and bounds changed significantly, pause and seek to new start
      // This ensures the player is ready to play the new selection without glitches
      if (trimModeActive && boundsChangedSignificantly) {
        // Pause first to avoid audio glitches during seek
        if (trimPlayer.isPlaying) {
          trimPlayer.pause();
        }
        // Seek to new start position - await to ensure it completes
        await trimPlayer.seek(start);
      }
    },
    [trimModeActive, trimPlayer]
  );

  // Handle entering/exiting trim mode
  const handleTrimModeChange = useCallback(
    (active: boolean) => {
      if (active) {
        // Entering trim mode - pause global player
        if (isThisSoundPlaying) {
          globalPlayer.pause();
        }
        setTrimModeActive(true);
        // Note: Auto-play is handled by the effect below after trimPlayer initializes
      } else {
        // Exiting trim mode - stop trim player
        if (trimPlayer.isPlaying) {
          trimPlayer.pause();
        }
        setTrimModeActive(false);
      }
    },
    [isThisSoundPlaying, globalPlayer, trimPlayer]
  );

  const pickPart = useCallback(() => {
    handleTrimModeChange(true);
    const editor = trimmerRef.current;
    if (!editor) return;
    editor.scrollIntoView({ block: "start", behavior: prefersReducedMotion ? "auto" : "smooth" });
    editor.focus({ preventScroll: true });
  }, [handleTrimModeChange, prefersReducedMotion]);

  // Seek trim player to the start position when entering trim mode.
  // Do NOT auto-play: setTimeout breaks the user-gesture chain on mobile
  // Safari, causing NotAllowedError on every iOS device.
  //
  // `trimPlayer` is a fresh object on every render (useAudioPlayer returns an
  // unmemoized object), so it must NOT be an effect dependency — with it in
  // the deps this effect re-ran on every timeupdate render during trim
  // playback and its setTimeout re-seeked playback back to trimStart in a
  // loop. Instead the latest player (and trimStart, so a bounds tweak inside
  // the 100ms window can't cancel the entry seek by re-running the effect)
  // live in refs, and the effect keys only on the mode transition, latching
  // prevTrimModeRef unconditionally so it records every run. Net effect:
  // exactly one seek per trim-mode entry, timer cleared on cleanup.
  const trimPlayerRef = useRef(trimPlayer);
  const trimStartRef = useRef(trimStart);
  useEffect(() => {
    trimPlayerRef.current = trimPlayer;
    trimStartRef.current = trimStart;
  }, [trimPlayer, trimStart]);
  const prevTrimModeRef = useRef(trimModeActive);
  useEffect(() => {
    const wasActive = prevTrimModeRef.current;
    prevTrimModeRef.current = trimModeActive;
    if (trimModeActive && !wasActive) {
      const timer = setTimeout(() => {
        trimPlayerRef.current.seek(trimStartRef.current);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [trimModeActive]);

  // Track sound detail page view for conversion funnel
  useEffect(() => {
    trackSoundPageViewed({
      sound_id: sound.id,
      sound_title: sound.title,
      category: sound.category,
    });
  }, [sound.id, sound.title, sound.category]);

  // Handle play/pause in trim mode
  const handleTrimPlayPause = useCallback(async () => {
    if (trimPlayer.isPlaying) {
      trimPlayer.pause();
    } else {
      // Always seek to trim start before playing to ensure correct position
      // This handles cases where:
      // 1. Audio was paused at boundary end
      // 2. User changed trim selection while paused
      // 3. Audio position drifted outside bounds
      // 4. Multiple trim selection changes in a row
      // Await the seek to complete before playing (seek now returns a Promise)
      await trimPlayer.seek(trimStart);
      await trimPlayer.play();
    }
  }, [trimPlayer, trimStart]);

  // Play/Pause handler using global context
  const handlePlayPause = useCallback(() => {
    // Loading counts as "engaged" so a second activation during the load
    // cancels it rather than re-issuing play(). Keeps the control a strict
    // toggle for keyboard users, who can now press the key twice in a row
    // without the button blurring out from under them (TES-1579).
    if (isThisSoundPlaying || isThisSoundLoading) {
      globalPlayer.pause();
    } else {
      globalPlayer.play(sound);
      trackPreviewPlay({
        sound_id: sound.id,
        sound_title: sound.title,
        category: sound.category,
        source: "sound_player",
      });
    }
  }, [isThisSoundPlaying, isThisSoundLoading, globalPlayer, sound]);

  const handleToggleSaved = () => {
    const saved = toggleFavorite(sound.id);

    if (saved) {
      trackSavedSoundSaveClick({ soundId: sound.id, source: "sound_player" });
    } else {
      trackSavedSoundUnsaveClick({ soundId: sound.id, source: "sound_player" });
    }

    const notice = saved ? "Saved." : "Removed.";
    setInteractionNotice(notice);
    announce(notice, "polite");
  };

  const handleShare = async () => {
    trackReferralLinkGenerated({
      sound_id: sound.id,
      share_surface: 'header_button',
    });
    // Canonical slug URL + the shared utm scheme — raw location.href carried
    // no attribution, making header shares invisible in funnel data.
    const shareUrl = buildSurfaceShareUrl(
      `${SITE_URL}/sounds/${sound.slug ?? sound.id}`,
      'header_button'
    );
    try {
      await navigator.share({
        title: `${sound.title} - Tesla Lock Sound`,
        text: `Check out this Tesla lock sound: ${sound.title}`,
        url: shareUrl,
      });
      trackReferralLinkCopied({
        sound_id: sound.id,
        share_surface: 'header_button',
        share_method: 'native_share',
      });
      play('success');
    } catch (error) {
      // User cancelled the native share sheet — don't fall through to clipboard
      if ((error as DOMException)?.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(shareUrl);
        setInteractionNotice("Link copied.");
        announce("Link copied.", "polite");
        trackReferralLinkCopied({
          sound_id: sound.id,
          share_surface: 'header_button',
          share_method: 'clipboard',
        });
        // A copied link is a lighter outcome than a saved file, so it gets
        // the soft confirmation tink rather than the three-note `success`.
        play('chime');
      } catch {
        play('error');
      }
    }
  };

  // Get current playback values from context or defaults
  const currentTime = isThisSoundActive ? globalPlayer.currentTime : 0;
  // One source for the length, so the readout and the spec sheet agree and the
  // duration does not change when playback starts and the element reports its
  // own decoded length.
  const duration = sound.duration || (isThisSoundActive ? globalPlayer.duration : 0) || 3;
  const progress = isThisSoundActive ? globalPlayer.progress : 0;

  /**
   * Rule 3's live level: the peak amplitude AT the playhead.
   *
   * There is no analyser node in this app, and adding one for a decorative
   * readout would mean a Web Audio graph, a CORS-tainted source to worry
   * about, and a second thing that can fail on autoplay. It is not needed:
   * waveform_data already holds the per-slice peaks the waveform draws, so
   * sampling it at the current progress gives the real amplitude of the audio
   * being heard right now, from data already on the page.
   *
   * undefined rather than 0 when nothing is playing — the plate renders no
   * overlay at all in that case, rather than an empty meter.
  */
  const signalLevel = (() => {
    // The title signal is emphasis, not the playback-state carrier. Under a
    // reduced-motion preference, omit the overlay entirely; the transport,
    // timeline, and base title remain present and readable.
    if (prefersReducedMotion) return undefined;
    if (!isThisSoundPlaying) return undefined;
    const peaks = sound.waveform_data;
    if (!Array.isArray(peaks) || peaks.length === 0) return undefined;
    // `progress` is a 0..100 percentage (see globalPlayer.progress /
    // useAudioPlayer's `progress` and AudioWaveform's identical prop, which
    // both divide by 100 the same way) — not a 0..1 fraction.
    const i = Math.min(peaks.length - 1, Math.max(0, Math.floor((progress / 100) * peaks.length)));
    const peak = Number(peaks[i]);
    if (!Number.isFinite(peak)) return undefined;
    // Stored peaks are 0..1 in this catalog, but a few rows carry 0..100.
    return peak > 1 ? peak / 100 : peak;
  })();
  const downloadCount = visibleDownloadCount(liveDownloads);
  const isLoading = isThisSoundLoading;
  const hasError = isThisSoundActive ? globalPlayer.error : null;

  const nextStepRowClass =
    "group flex min-h-24 items-center border-t border-[color:var(--tls-border-subtle)] py-5 transition-colors duration-[var(--motion-duration-fast)] hover:border-[color:var(--tls-border-strong)] hover:bg-[var(--tls-surface-hover)]";

  // §5.1's per-category hero register is deliberately NOT applied to the title.
  // The plate owns its own register, for one design reason and one mechanical
  // one.
  //
  // The design reason is the system's second rule: the plate is the expanded
  // state of a row, so a row and a plate have to be the same object. A title
  // whose weight and tracking change by category breaks that identity.
  //
  // The mechanical reason is the one that settles it. The solver's advance-width
  // constant is calibrated at the plate's -0.05em tracking. The `wide` and
  // `urgent` registers set +0.04em and +0.06em, a swing of about 0.1em, which
  // moves advance width by double digits and makes the server pass solve for a
  // string that will not render. The client would recover by measurement, but
  // only after a worse first paint.
  //
  // `heroRegister` therefore has no consumer right now. The data stays in
  // lib/category-data.ts because it records real design intent, and a future
  // register that varies something the plate does not own (colour, rule weight,
  // the signal treatment) could use it without fighting the solver.

  // Install/duration hints (§5.2, §5.7).
  const durationHint = getDurationHint(sound);
  const installHint = getInstallHint(sound);

  return (
    <LazyMotion features={domAnimation}>
    {/* translate="no" (TES-2345): the third route in the TES-1982/TES-1986
        browser-translation crash family, and the last reconciliation-heavy
        one left uncovered after /sounds (PR 998) and /sounds/category/[slug]
        (PR 1177). A page translator re-parents text nodes into font wrappers
        on every pass. React does not know about the move, so the next
        commit's DOM ops throw (removeChild NotFoundError, surfacing as React
        error 310), or, once PR 993's no-op guard leaves the stale nodes
        behind instead of removing them, accumulate across a session into the
        RangeError variant. Korean was one of the three original repro
        locales.

        The attribute sits on the component root rather than on an inner
        subtree because there is no static region below it to protect: the
        breadcrumb source resolves client-side, the transport swaps the CTA
        for the receipt through AnimatePresence, the trim editor and both
        modals mount and unmount, and the sticky CTA is a sibling of the
        animated body. Anything narrower leaves the removal-heavy subtrees
        (the ones that actually throw) outside the boundary.

        Cost, stated plainly: a translated session no longer gets this
        component's body copy in its own language. TES-2342 measured Naver
        (Korean) as the top non-search referring domain, converting at search
        parity on the English page already, with landings overwhelmingly on
        these detail pages, so a hard crash is the larger of the two risks.
        The sound title, the spec values and LockChime.wav are strings a
        translator should never have touched anyway. The server-rendered SEO
        section and the hub/collection links in app/sounds/[id]/page.tsx sit
        outside this component and stay translatable. */}
    <div className="min-h-screen" translate="no" style={{ background: "var(--tls-canvas)" }}>
      {/* TES-831: initial={false} makes Framer Motion render the `visible`
          variant during SSR instead of opacity:0. Without this the LCP <h1>
          ships at opacity:0 and only paints after the full client bundle
          hydrates (measured 14–20s LCP on mobile 4G). Variant propagation
          means children render visible too; the entrance stagger is
          intentionally dropped here because LCP < 2.5s is a non-negotiable
          perf pillar and a hydration-gated opacity:0 over all above-the-fold
          content is the root cause, not tasteful motion. */}
      <m.div
        variants={containerVariants}
        initial={false}
        animate="visible"
        className="pb-32 tls-clears-nav"
      >
        {/* Return-visit install check-in (install-success loop) — renders only
            when a 2h-14d-old pending download exists in localStorage. */}
        <div className="tls-container">
          <InstallCheckin className="mt-3" />
        </div>

        {/* Breadcrumb strip. The band's hairline is full-bleed; its content
            shares the site's one column axis (.tls-container: 1440 cap +
            the shared 32/16 gutter, x=32 at 1440). */}
        <m.div
          variants={itemVariants}
          className="py-[18px]"
          style={{
            borderBottom: "1px solid var(--tls-border-subtle)",
          }}
        >
          <div className="tls-container relative flex items-center gap-3">
            {/* Visible half of the Save / Share confirmation. The live region
                (announce) carries it for screen readers; this sits in the
                hero's top inset so it moves nothing when it appears. */}
            {interactionNotice && (
              <p
                aria-hidden="true"
                className="absolute right-4 top-[calc(100%+var(--space-8))] text-xs leading-none sm:right-8"
                style={{ color: "var(--tls-fg-muted)" }}
              >
                {interactionNotice}
              </p>
            )}
            {/* The site's one breadcrumb. It carries the category, so the title
                block below has no category label above it. The JSON-LD
                BreadcrumbList in app/sounds/[id]/page.tsx is the SEO trail
                and lists the same four levels. Below `sm` the page's own crumb
                is clipped (still read aloud): the h1 under it states it. */}
            <Breadcrumb
              className="min-w-0 flex-1"
              collapseLastBelowSm
              crumbs={[
                { label: "Home", href: "/" },
                { label: breadcrumbSource.label, href: breadcrumbSource.href },
                { label: sound.category, href: categoryBrowseHref(sound.category) },
                { label: sound.title },
              ]}
            />

            {/* Action icons */}
            <div className="flex items-center gap-2 shrink-0">
              <button type="button"
                onClick={handleToggleSaved}
                className={cn(
                  iconButtonClass,
                  isSaved ? "border-brand/30 bg-brand/10 text-[var(--tls-brand-ink)]" : ""
                )}
                aria-label="Save sound"
                aria-pressed={isSaved}
                title={isSaved ? "Remove from saved sounds" : "Save sound"}
              >
                <Heart className={`w-4 h-4 ${isSaved ? "fill-current" : ""}`} />
              </button>
              <button type="button"
                onClick={() => setShowReportModal(true)}
                className={cn(iconButtonClass, "hover:text-[var(--tls-fg-strong)]")}
                aria-label="Report issue"
                title="Report an issue with this sound"
              >
                <Flag className="size-4" />
              </button>
              <button type="button"
                onClick={handleShare}
                data-cuelume-press="press"
                data-cuelume-release="scan"
                className={cn(
                  iconButtonClass,
                  "hover:border-brand/30 hover:bg-brand/5 hover:text-[var(--tls-brand-ink)]"
                )}
                aria-label="Share"
              >
                <Share2 className="size-4" />
              </button>
            </div>
          </div>
        </m.div>

        {/* Hero player — 1-col on mobile, 2-col at lg */}
        {/* The closing hairline sits on the INNER box so it spans exactly the
            content column (x=32..1408 at 1440), not the padded container. */}
        <section className="tls-container">
        {/* The top inset is smaller below `sm` because the breadcrumb band
            above already closes with 18px of padding and a hairline: 40 on top
            of that put 58px of empty canvas between a rule and the first glyph,
            on the one screen where vertical space converts.

            This is what pays for the breadcrumb clamp above. Measured across 12
            sounds spanning the title-length distribution, the clamp's second
            line costs 17px and this returns 16, so the play control moves +1px
            and truncation goes from 9 of 12 to 0 of 12. It is not a fold win on
            its own and should not be quoted as one: the plate still does not
            start until y=280, which is 34% of a 375×812 fold and 42% of an
            iPhone SE's 667px fold, where the Download CTA stays clipped on 8 of
            12. Closing that needs the stack above the plate redesigned, not
            another 16px.

            Desktop keeps 40: there the hero shares its row with the player and
            the band is not stacked against the fold. */}
        <div
          className="grid grid-cols-1 gap-10 pt-6 sm:pt-10 lg:grid-cols-[1fr_360px]"
          style={{
            paddingBottom: 40,
            borderBottom: "1px solid var(--tls-border-subtle)",
          }}
        >
          {/* LEFT: Play surface.

              A flex column so that below `sm` the order can differ from the
              DOM: meta row, title, install warnings, then the waveform band
              (which puts the transport first at that width), then the
              description. At 390×844 the play button of the most-downloaded
              sound sat at y=816, cut by the fold, with the description and
              the strip both above it; on desktop the DOM order stands. The
              warnings stay above the band at every width because they are
              read BEFORE the file is taken. Flex margins do not collapse, but
              every child here carries a one-sided margin, so block flow and
              flex flow measure the same. */}
          <m.div variants={itemVariants} className="flex flex-col">
            {/* Title, Plate & Hairline. See PRODUCT.md and §21.
                The h1's TEXT CONTENT is unchanged: the display portion and the
                parenthetical are both still inside it, in the same order, so
                what a crawler reads is byte-identical to before. Only the
                visual hierarchy changed. The display portion takes the plate
                treatment and the descriptor drops to a subhead, which is the
                rule that makes giant type survive a 39-character median.

                This was the pilot and hand-assembled its own heading. It uses
                the shared component now: the descriptor had already drifted to
                weight 500 against PlateHeading's 600, which is exactly the
                divergence a shared component exists to prevent. */}
            <PlateHeading
              className="-order-3 sm:order-none"
              style={{ marginBottom: 14 }}
              usable={358}
              level={signalLevel}
            >
              {sound.title}
            </PlateHeading>

            {/* Trailing meta, under the title: data and state only. The
                category lives in the breadcrumb and the related rail's "All
                {category} sounds" link, not in a label above the plate.
                1 to 24 installs is left out: "2 installs" on a long-tail
                sound reads as a reason not to download. Zero keeps its
                section 02.9 state. */}
            {(downloadCount || isThisSoundPlaying) && (
              <div
                className="-order-3 sm:order-none"
                style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginTop: -6, marginBottom: 14 }}
              >
                {downloadCount && <Mono size={11}>{`${downloadCount.value} ${downloadCount.unit.toLowerCase()}`}</Mono>}
                {downloadCount && isThisSoundPlaying && (
                  <Mono size={11} color="var(--tls-fg-subtle)" aria-hidden>
                    ·
                  </Mono>
                )}
                {isThisSoundPlaying && <Chrome color="var(--tls-fg-strong)">now playing</Chrome>}
              </div>
            )}

            {isMatureSound(sound) && (
              <p
                className="-order-3 mb-3 text-sm font-bold sm:order-none"
                style={{ color: "var(--tls-fg-strong)" }}
              >
                {MATURE_LABEL_DETAIL}
              </p>
            )}

            {/* Description */}
            {sound.description && (
              <p
                style={{
                  font: "var(--tls-body-lg)",
                  color: "var(--tls-fg-muted)",
                  maxWidth: "65ch",
                  margin: "0 0 12px",
                }}
              >
                {sound.description}
              </p>
            )}

            {/* TES-2556: one-line cross-link for a near-duplicate pair that
                splits the same query, so a visitor who lands on the wrong
                page has one click to the one they meant. */}
            {crossLink && (
              <p style={{ font: "var(--tls-body-sm)", color: "var(--tls-fg-muted)", maxWidth: "65ch", margin: "0 0 32px" }}>
                {crossLink.label}{" "}
                <Link
                  href={crossLink.href}
                  style={{ color: "var(--tls-fg-strong)", textDecoration: "underline" }}
                  className="transition-colors hover:text-[color:var(--tls-brand-ink)]"
                >
                  Try this one
                </Link>
              </p>
            )}

            {/* Format/size install warnings. Unconditional: these are
                properties of the SOUND, so they reach every visitor rather
                than only the ones who had filled in a vehicle form. Renders
                nothing for a file that installs as shipped. */}
            <InstallWarnings
              sound={sound}
              deliversTrimmedClip={needsTrimming}
              className="-order-2 sm:order-none"
            />

            {/* Waveform. De-carded: this is the signal, which rule 3 makes the
                most important content on the page, and it was sitting in a
                filled bordered box. Rules top and bottom give it its own band
                without framing it.

                Below `sm` the transport row (play + Download) is the FIRST
                thing in the band, above the strip,
                and the band itself sits above the description (see the column
                comment). Desktop keeps strip → transport: there the
                play surface shares its row with the spec sheet and the
                transport is the natural foot of the band. */}
            <div
              className="-order-1 flex flex-col sm:order-none"
              style={{
                padding: "28px 0",
                borderTop: "1px solid var(--tls-border-subtle)",
                borderBottom: "1px solid var(--tls-border-subtle)",
              }}
            >
              {/* One signal strip doubles as the scrubber: the waveform stays
                  visible, while the keyboard/pointer slider owns the input.
                  Nothing overlays it: a tap here starts playback (onSeekCommit
                  below), but on mobile the strip is reached only after the
                  56px play button and the Download CTA, so a centred label
                  advertising the same action bought nothing and cost the
                  signal a bordered, filled box across its middle. */}
              <div
                // From sm the band's own top rule sits directly above the
                // strip, so the strip drops its top rule there.
                className={cn(
                  "order-1 border-y border-[color:var(--tls-border-subtle)] py-2 sm:order-none sm:border-t-0",
                  trimModeActive && "opacity-50"
                )}
              >
                {/* §03.3: the strip IS the scrub bar. Peaks come from the
                    stored waveform_data only; nothing decodes the file to
                    draw it. */}
                <PlayerSeek
                  className={trimModeActive ? "pointer-events-none" : undefined}
                  title={sound.title}
                  peaks={sound.waveform_data}
                  duration={duration}
                  currentTime={trimModeActive ? 0 : currentTime}
                  playhead={!trimModeActive && isThisSoundActive}
                  interactive={!trimModeActive}
                  onSeekCommit={time => {
                    if (!isThisSoundActive) {
                      // A tap on the strip is a preview start, not only a
                      // seek; count it the same as the play button.
                      globalPlayer.play(sound);
                      trackPreviewPlay({
                        sound_id: sound.id,
                        sound_title: sound.title,
                        category: sound.category,
                        source: "sound_player",
                      });
                    }
                    globalPlayer.seek(time);
                  }}
                />
                {/* Plain text, not a live region: position reaches assistive
                    tech through the slider's value text on each seek. */}
                <div className="mt-2 flex justify-between">
                  <PlayerTime className="text-start" decimals={2} currentTime={trimModeActive ? 0 : currentTime} duration={duration} />
                  <PlayerTime type="duration" decimals={2} duration={duration} />
                </div>
              </div>

              {/* Play controls row. The second cell is the Download CTA until
                  the download completes, then the inline receipt takes the
                  same slot: the confirmation lands exactly where the button
                  was, inside the fold at scroll 0, instead of mounting after
                  the band and below it (measured at y=894 on a 900px
                  viewport, with focus dropped to <body>). `mode="wait"` lets
                  the button fade out before the receipt mounts and moves
                  focus to its heading. */}
              <div
                className={cn(
                  "order-first mb-5 grid grid-cols-1 gap-3 sm:order-none sm:mb-0 sm:mt-6 sm:grid-cols-[56px_minmax(0,1fr)] sm:gap-y-0",
                  downloadCompleted ? "sm:items-start" : "sm:items-center"
                )}
              >
                {/* Stable "Play <title>" name + aria-pressed (TES-1581). */}
                <div className="sm:col-start-1 sm:row-start-2">
                  <TPlay
                    onClick={handlePlayPause}
                    playing={isThisSoundPlaying}
                    loading={isLoading}
                    title={sound.title}
                    size={56}
                  />
                </div>
                {/* One notice for a long sound, ahead of the button it
                    explains. The button below already cuts the first 5 s. */}
                {needsTrimming && durationHint && !downloadCompleted && (
                  <div className="min-w-0 sm:col-start-2 sm:row-start-1 sm:mb-3">
                    <p className="text-sm font-bold" style={{ color: "var(--tls-fg-strong)" }}>
                      {durationHint}
                    </p>
                    <button
                      type="button"
                      onClick={pickPart}
                      className="tlsr-focus mt-1 inline-flex min-h-11 items-center text-sm font-medium underline underline-offset-2"
                      style={{ color: "var(--tls-fg-muted)" }}
                    >
                      Pick the part you want
                    </button>
                  </div>
                )}
                <AnimatePresence mode="wait" initial={false}>
                  {!downloadCompleted ? (
                    <m.button
                      key="download-btn"
                      // initial={false}, for the same reason the container
                      // above sets it: `initial={{ opacity: 0 }}` ships the
                      // primary CTA at opacity:0 in the SSR HTML and only
                      // paints it once the client bundle hydrates. It was also
                      // focusable while invisible. Nothing has changed on
                      // first paint, so there is nothing for a fade to confirm
                      // (PRODUCT.md principle 6); the exit fade stays because
                      // the download completing is a state change.
                      initial={false}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: MOTION_DURATION.fast, ease: MOTION_EASE.standard }}
                      onClick={handleDownload}
                      data-cuelume-press="press"
                      data-cuelume-release="pulse"
                      disabled={isDownloading}
                      // Label in name (SC 2.5.3). A short sound has no
                      // aria-label: its visible text, size included, is the
                      // name. A long sound's visible text leads its name:
                      // "Download first 5 seconds, LockChime.wav". While the
                      // file is downloading, the visible "Downloading…" is the
                      // name on both.
                      aria-label={
                        needsTrimming && !isDownloading
                          ? `Download ${trimSpan}, LockChime.wav`
                          : undefined
                      }
                      // Button `lg` per §02.1: 52px min-height, 16/40 padding,
                      // 16px type at heading weight; the trailing file size
                      // sets the expectation before the tap (DS Button
                      // `trailing`).
                      // px-6 below sm: at 390 the 40px inline padding left only
                      // 278px of content box, so "Download LockChime.wav · 335
                      // KB" wrapped to two lines and grew the pill to 72px.
                      className="tlsr-focus flex min-h-[52px] w-full items-center justify-center gap-2 sm:col-start-2 sm:row-start-2 whitespace-nowrap rounded-[var(--tls-radius-pill)] px-6 py-4 text-base font-bold leading-[1.2] transition-[background-color,opacity] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed sm:px-10"
                      style={{
                        background: "var(--tls-brand)",
                        color: "var(--tls-brand-fg)",
                        border: "none",
                      }}
                    >
                      {isDownloading ? (
                        <>
                          <Spinner size="sm" />
                          Downloading…
                        </>
                      ) : (
                        <>
                          <Download className="size-4" />
                          {needsTrimming ? `Download ${trimSpan}` : "Download LockChime.wav"}
                          {!needsTrimming && ctaTrailing && " "}
                          {!needsTrimming && ctaTrailing && (
                            // Part of the accessible name: the name is the
                            // visible text, so voice control ("click Download
                            // LockChime.wav") matches (SC 2.5.3).
                            <span
                              // Dimmed typographically (size + weight), never
                              // by opacity: opacity-72 on brand-fg measured
                              // below 4.5:1 (TES-1580).
                              className="tabular-nums text-sm font-semibold"
                            >
                              · {ctaTrailing}
                            </span>
                          )}
                        </>
                      )}
                    </m.button>
                  ) : (
                    /* Post-download receipt — primary success surface
                       (TES-1238 / G3.3). Takes the CTA's slot; the install
                       path stays visible, no modal (PRODUCT.md Principle #5).
                       The receipt itself is role=status and moves focus to
                       its heading on mount (§05.3). */
                    <m.div
                      key="post-download-receipt"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: MOTION_DURATION.emphasis }}
                      className="min-w-0 sm:col-start-2 sm:row-start-2"
                    >
                      <PostDownloadReceipt
                        sound={receiptSound}
                        followOnSound={followOnSound}
                        onCreateShareCard={() => setShowShareSetup(true)}
                        onDownloadAgain={handleDownload}
                        isDownloading={isDownloading}
                        downloadError={downloadError}
                        headingRef={receiptHeadingRef}
                      />
                    </m.div>
                  )}
                </AnimatePresence>
                {!downloadCompleted && (
                  <div className="flex min-w-0 flex-col gap-3 sm:col-start-2 sm:row-start-3 sm:mt-3">
                    {/* Failures are said directly under the button, in the same
                        words the live region carries (lib/state-copy.ts). */}
                    <AnimatePresence>
                      {downloadError && (
                        <m.p
                          key="download-error"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="m-0 text-sm font-bold"
                          style={{ color: "var(--tls-fg-strong)" }}
                          data-testid="download-error"
                          ref={revealNote}
                        >
                          {downloadError}
                        </m.p>
                      )}
                      {hasError && (
                        <m.p
                          key="playback-error"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="m-0 text-sm font-bold"
                          style={{ color: "var(--tls-fg-strong)" }}
                          data-testid="playback-error"
                          ref={revealNote}
                        >
                          {previewFailureMessage(hasError)}
                        </m.p>
                      )}
                    </AnimatePresence>
                    <p className="text-sm" style={{ color: "var(--tls-fg-muted)" }}>
                      Plain .wav audio file. No installer, no account.
                    </p>
                    <div className="hidden touch-device:block">
                      <p className="text-sm leading-snug" style={{ color: "var(--tls-fg-muted)" }}>
                        Finishing on a computer or a USB-C stick?{" "}
                        <button
                          type="button"
                          onClick={() => setPhoneEmailOpen(v => !v)}
                          aria-expanded={phoneEmailOpen}
                          // Inline so the sentence keeps one line height; the ::before pads the
                          // tap target.
                          className="tlsr-focus relative font-medium underline underline-offset-2 before:absolute before:inset-x-0 before:-inset-y-3.5 before:content-['']"
                          style={{ color: "var(--tls-fg-strong)" }}
                        >
                          Email it to me
                        </button>
                      </p>
                      <EmailSoundForm
                        soundId={sound.id}
                        surface="sound_page"
                        label="Email it to me"
                        hideTrigger
                        open={phoneEmailOpen}
                        onOpenChange={setPhoneEmailOpen}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Email-me bridge — pre-download escape hatch for mobile users
                without a USB drive at hand (plan item 2). Hidden once the
                download completes; the receipt carries its own copy. */}
            {!downloadCompleted && (
              <div className="mt-4 touch-device:hidden">
                <EmailSoundForm
                  soundId={sound.id}
                  surface="sound_page"
                  emphasis="primary"
                  label="Email this sound to yourself"
                />
              </div>
            )}

            {/* Chime Studio entry link — lets users open this sound directly in the editor */}
            <div style={{ marginTop: 16 }}>
              <OpenInChimeStudio soundId={sound.id} variant="subtle" className="text-sm" />
            </div>

            {/* Audio Trimmer for sounds longer than 5 seconds */}
            {needsTrimming && (
              <m.div
                variants={itemVariants}
                className="tlsr-focus mt-6"
                ref={trimmerRef}
                role="group"
                aria-label="Trim editor"
                tabIndex={-1}
              >
                <AudioTrimmer
                  duration={sound.duration || 0}
                  waveformData={sound.waveform_data}
                  currentTime={
                    trimModeActive ? trimPlayer.currentTime : isThisSoundActive ? currentTime : 0
                  }
                  isPlaying={trimModeActive ? trimPlayer.isPlaying : isThisSoundPlaying}
                  onTrimChange={handleTrimChange}
                  onSeek={time => {
                    if (trimModeActive) {
                      trimPlayer.seek(time);
                    } else {
                      if (!isThisSoundActive) globalPlayer.play(sound);
                      globalPlayer.seek(time);
                    }
                  }}
                  onPlayPause={trimModeActive ? handleTrimPlayPause : handlePlayPause}
                  trimModeActive={trimModeActive}
                  onTrimModeChange={handleTrimModeChange}
                  loopEnabled={loopEnabled}
                  onLoopChange={setLoopEnabled}
                />
                {/* The editor sits far below the main Download button, so the
                    chosen part is downloadable from right here. */}
                {!downloadCompleted && trimEdited && (
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    aria-label={
                      isDownloading
                        ? "Downloading… from the trim editor"
                        : `Download ${trimSpan}, LockChime.wav, from the trim editor`
                    }
                    className="tlsr-focus mt-4 flex min-h-13 w-full items-center justify-center gap-2 whitespace-nowrap rounded-[var(--tls-radius-pill)] px-6 py-4 text-base font-bold leading-[1.2] disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ background: "var(--tls-brand)", color: "var(--tls-brand-fg)", border: "none" }}
                  >
                    {isDownloading ? (
                      <>
                        <Spinner size="sm" />
                        Downloading…
                      </>
                    ) : (
                      <>
                        <Download className="size-4" />
                        Download {trimSpan}
                      </>
                    )}
                  </button>
                )}
              </m.div>
            )}
          </m.div>

          {/* RIGHT: Info panel / spec sheet */}
          <m.div variants={itemVariants}>
            <SpecSheetPanel
              sound={sound}
              liveDownloads={liveDownloads}
              downloadCompleted={downloadCompleted}
              installHint={installHint}
              delivered={{ seconds: deliveredSeconds, sizeLabel: deliveredSizeLabel }}
              onShare={handleShare}
              onCreateShareCard={() => setShowShareSetup(true)}
              // TES-1238: the inline receipt below the waveform is the single
              // post-download surface; suppress the sidebar's duplicate panel.
              showPostDownloadPanel={false}
            />
          </m.div>
        </div>
        </section>

        {/* Below the fold */}
        <div className="tls-container space-y-12 mt-12">
          {/* The page's one ad slot is mounted by app/sounds/[id]/page.tsx, below
              the related rail and the matching collections. Do not add a second
              mount of slot 1065739730 here (TES-2137). */}

          {/* Related sounds (density-2 hairline list); renders null when relatedSounds is empty */}
          <RelatedSoundsRail
            sound={sound}
            relatedSounds={relatedSounds}
            relatedRailTitle={relatedRailTitle}
          />

          {/* Next Steps */}
          <m.div variants={itemVariants}>
            <div className="grid sm:grid-cols-2 sm:gap-x-8">
              <Link href="/guide" className={nextStepRowClass}>
                <div className="min-w-0">
                  <div className="font-semibold" style={{ color: "var(--tls-fg-strong)" }}>
                    Installation guide
                  </div>
                  <div className="mt-1 text-sm" style={{ color: "var(--tls-fg-muted)" }}>
                    The full USB walkthrough, with a page for each model
                  </div>
                </div>
              </Link>
              <Link href="/convert" className={nextStepRowClass}>
                <div className="min-w-0">
                  <div className="font-semibold" style={{ color: "var(--tls-fg-strong)" }}>
                    Convert your own audio
                  </div>
                  <div className="mt-1 text-sm" style={{ color: "var(--tls-fg-muted)" }}>
                    Trim, fade and export your own clip as LockChime.wav
                  </div>
                </div>
              </Link>
            </div>
          </m.div>
        </div>
      </m.div>

      {/* Report Modal — only mount when open to avoid unnecessary dynamic load */}
      {showReportModal && (
        <ReportModal
          isOpen
          onClose={() => setShowReportModal(false)}
          sound={sound}
        />
      )}

      {/* Share Setup Modal */}
      <AnimatePresence>
        {showShareSetup && (
          <ShareSetupPanel sound={sound} onClose={() => setShowShareSetup(false)} />
        )}
      </AnimatePresence>

      {/* §5.3 — sticky mobile install CTA. Mobile-only (`sm:hidden`),
          appears after 40% scroll so the sacred install path is never more
          than a thumb away. Label unchanged from the primary CTA per
          §5.3 / §9 until the download completes; then it points at the
          receipt's install steps instead of re-offering the file. */}
      {!downloadOnly && (
        <StickyInstallCta
          isDownloading={isDownloading}
          onDownload={handleDownload}
          downloadCompleted={downloadCompleted}
          onShowInstallSteps={showInstallSteps}
        />
      )}
    </div>
    </LazyMotion>
  );
}
